import {
  LEFT_COLUMN_WIDTH_PX,
  WHEEL_ZOOM_SENSITIVITY,
  ZOOM_MAX_VISIBLE_DAYS,
  ZOOM_MIN_VISIBLE_DAYS,
} from './timelineGeometry';

/**
 * Zoom scale math — every conversion between the user's zoom gestures and the
 * day width the timeline renders at.
 *
 * All of it is pure: viewport/pointer numbers in, a day width or scroll offset
 * out. The DOM measurement and event plumbing live in `useTimelineZoom.ts`; the
 * anchor formulas live here so the "the day under the cursor stays put" rule is
 * unit-assertable without a browser.
 */

export type ZoomBounds = { minDayWidth: number; maxDayWidth: number };

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * The realistic zoom range for a given track viewport: fully zoomed in shows a
 * week, fully zoomed out a year — or the whole window when the plan is shorter,
 * so the track never becomes narrower than the viewport it scrolls in.
 *
 * A viewport under 100px means the panel is collapsed or not yet measured; the
 * fallback keeps the math finite until the ResizeObserver reports a real width.
 */
export const zoomBounds = (trackViewportPx: number, dayCount: number): ZoomBounds => {
  if (trackViewportPx < 100) {
    return { minDayWidth: 2, maxDayWidth: 100 };
  }
  return {
    minDayWidth: trackViewportPx / Math.min(ZOOM_MAX_VISIBLE_DAYS, Math.max(1, dayCount)),
    maxDayWidth: trackViewportPx / ZOOM_MIN_VISIBLE_DAYS,
  };
};

/**
 * Snap a day width to 0.5px steps (bars and gridlines stay near pixel register;
 * the 7px default is exactly representable), then clamp — clamping last keeps
 * both bounds exactly reachable even when they are not multiples of 0.5.
 */
export const quantizeDayWidth = (dayWidth: number, bounds: ZoomBounds): number =>
  clamp(Math.round(dayWidth * 2) / 2, bounds.minDayWidth, bounds.maxDayWidth);

/**
 * Slider position → day width, geometrically: the range spans ~50×, so equal
 * slider travel means equal zoom *factor*, not equal px. t is 0 (year) … 1 (week).
 */
export const sliderToDayWidth = (t: number, bounds: ZoomBounds): number =>
  quantizeDayWidth(
    bounds.minDayWidth * Math.pow(bounds.maxDayWidth / bounds.minDayWidth, clamp(t, 0, 1)),
    bounds,
  );

/** Day width → slider position; inverse of `sliderToDayWidth` up to quantization. */
export const dayWidthToSlider = (dayWidth: number, bounds: ZoomBounds): number =>
  bounds.maxDayWidth <= bounds.minDayWidth
    ? 0
    : clamp(
        Math.log(dayWidth / bounds.minDayWidth) / Math.log(bounds.maxDayWidth / bounds.minDayWidth),
        0,
        1,
      );

/**
 * One wheel event's worth of zoom, multiplicative so a notch feels the same at
 * every level. Callers normalize `deltaMode === 1` (line mode) to ~16px lines.
 */
export const wheelDayWidth = (current: number, deltaY: number, bounds: ZoomBounds): number =>
  quantizeDayWidth(current * Math.exp(-deltaY * WHEEL_ZOOM_SENSITIVITY), bounds);

/**
 * The (fractional) day sitting under a viewport x before a zoom — deliberately
 * unrounded, so restoring it after the zoom is exact rather than snapped to a
 * column edge.
 */
export const anchorDayAt = (scrollLeft: number, viewportX: number, dayWidth: number): number =>
  (scrollLeft + viewportX - LEFT_COLUMN_WIDTH_PX) / dayWidth;

/** The scrollLeft that puts `anchorDay` back under `viewportX` at the new zoom. */
export const zoomedScrollLeft = (
  anchorDay: number,
  viewportX: number,
  newDayWidth: number,
): number => Math.max(0, LEFT_COLUMN_WIDTH_PX + anchorDay * newDayWidth - viewportX);
